import { WarningSymbol } from '@/components/warning/WarningSymbol';
import { SeverityType } from '@/services/warnings/warningDefinitions';
import { RosterWarningModel } from '@/services/warnings/warnings.types';
import { FC, useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useAllWarnings } from '../providers/RosterWarningsService';
import strings from '../roster.translations';

const WarningsSection: FC<{ warnings: RosterWarningModel[]; major?: boolean }> = ({ warnings, major }) => {
  const [sectionOpen, setSectionOpen] = useState<boolean>(true);

  useEffect(() => {
    if (warnings.length === 0) {
      setSectionOpen(false);
    }
  }, [warnings]);

  return (
    <div className="py-2">
      <div className="space-x-3 flex" onClick={() => warnings.length > 0 && setSectionOpen(!sectionOpen)}>
        <button>{sectionOpen ? <FiChevronDown className="h-6 w-6" /> : <FiChevronRight className="h-6 w-6" />}</button>
        <div className="font-semibold text-xl">
          {major
            ? strings.drawer.warnings.majorIssues(warnings.length)
            : strings.drawer.warnings.minorIssues(warnings.length)}
        </div>
      </div>
      {sectionOpen && (
        <div className="mt-3 space-y-2 pl-5">
          {warnings.map((w, index) => (
            <div key={`${index}-${w.name}`} className="flex gap-3">
              <WarningSymbol className="h-6 w-6" severity={major ? SeverityType.CRITICAL : SeverityType.WARNING} />
              <div>
                <div className="font-semibold">{w.name}</div>
                {w.details && <div className="text-gray-600">{`(${w.details.join(', ')})`}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const RosterWarnings: FC = () => {
  const rosterWarnings = useAllWarnings();

  const [minorWarnings, majorWarnings] = useMemo(() => {
    const minorWarnings = rosterWarnings.filter(
      w => w.severity === SeverityType.NONE || w.severity === SeverityType.WARNING
    );
    const majorWarnings = rosterWarnings.filter(
      w => w.severity === SeverityType.CRITICAL || w.severity === SeverityType.SEMICRITICAL
    );

    return [minorWarnings, majorWarnings];
  }, [rosterWarnings]);

  return (
    <div className="space-y-2">
      <WarningsSection warnings={majorWarnings} major />
      <WarningsSection warnings={minorWarnings} />
    </div>
  );
};
